import {
  useClient,
  useRepresentativeClient as useRepresentativeClientApi,
} from 'api/requests/';
import { useCountries } from 'api/requests/country';
import { NavigationBreadcrumbsTitles } from 'components';
import { parseFullName } from 'helpers';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, RoutesId } from 'libs/navigation';
import { useRevokeOtpVerification } from 'modules/client/common/hooks';

import { RepresentativeFormType } from '../forms/representativeForm';
import { getRepresentativeInitialValue } from '../helpers';

import { useClientPageTitle } from './useClientPageTitle';
import { useLockRepresentativeClient } from './useLockRepresentativeClient';
import { useUpdateRepresentativeClient } from './useUpdateRepresentativeClient';

type RepresentativeClientContainerParams = {
  representativeId: string;
  clientId: string;
};

export const useRepresentativeClient = () => {
  const { t } = useTranslation();

  const { params, navigate } =
    useNavigate<RepresentativeClientContainerParams>();

  const { loading: loadingClient, client } = useClient(params.clientId!);

  const {
    loading: loadingRepresentativeClient,
    representativeClient,
    error,
    refetch,
  } = useRepresentativeClientApi(params.representativeId!);

  const loading = loadingClient || loadingRepresentativeClient;

  const { countries, loading: countriesLoading } = useCountries();

  const { updateClient, submitLoading } = useUpdateRepresentativeClient(client);

  const { lockClientIfPossible, unlockClient } =
    useLockRepresentativeClient(representativeClient);

  const { revokeOtpVerification, revokeOtpVerificationLoading } =
    useRevokeOtpVerification();

  const initialValues = representativeClient
    ? getRepresentativeInitialValue(representativeClient, t)
    : undefined;

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.personClient]: client ? parseFullName(client) : '',
    [RoutesId.representativeClient]: representativeClient
      ? t('client.representativeClient.title', {
          name: parseFullName(representativeClient),
        })
      : '',
  };

  const onSubmit: FormSubmit<RepresentativeFormType> = async (
    values,
    helpers
  ) => {
    if (await updateClient(representativeClient!, values)) {
      helpers.setReadOnly(true);
    }
  };

  const onShowVersionHistory =
    representativeClient?.versionNumber &&
    representativeClient?.versionNumber > 1
      ? () => {
          navigate(
            ROUTES.clients.personClients.personClient.personClientVersion
              .representativeClientVersion.fullPath,
            {
              clientId: params.clientId!,
              representativeId: params.representativeId!,
              versionId: representativeClient?.versionId!,
            }
          );
        }
      : undefined;

  const onRevokeOtpVerification = representativeClient?.user.otpVerified
    ? async () => revokeOtpVerification(representativeClient?.user.id!, refetch)
    : undefined;

  useClientPageTitle(client, representativeClient);

  return {
    client,
    representativeClient,
    loading: !representativeClient && loading,
    countries: countries ?? [],
    countriesLoading,
    error,
    initialValues,
    submitLoading,
    breadcrumbsTitles,
    revokeOtpVerificationLoading,
    lockClientIfPossible,
    unlockClient,
    onSubmit,
    onShowVersionHistory,
    onRevokeOtpVerification,
  };
};
